import React from 'react';
import { ExclamationCircleIcon, Text, themeVars, Select, TextField, Button } from '@eds-web/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StyledGatedContent } from './AgeGated.style';
import { useDateOfBirthForm } from '../../hooks/useDateOfBirthForm';
import { AgeGateProvider, useAgeGate } from '../../contexts/AgeGateContext';
import { Loading } from '../common/Loading';
import { confirmAge } from '../../actions/account.actions';
import { useLogoutRedirect } from '../../hooks/useLogoutRedirect';
import { selectIsConfirmingAge } from '../../selectors/account.selector';

interface AgeGateProps {
    onClose?: () => void;
    onSubmit?: (dob?: string) => void;
}

export const AgeGate: React.FC<AgeGateProps> = ({ onClose, onSubmit }) => {
    return (
        <AgeGateProvider>
            <AgeGateContent onClose={onClose} onSubmit={onSubmit} />
        </AgeGateProvider>
    );
};

interface AgeGateContentProps {
    onClose?: () => void;
    onSubmit?: (dob?: string) => void;
}

const AgeGateContent: React.FC<AgeGateContentProps> = ({ onClose, onSubmit }) => {
    const { isAgeValid, isConfirmingAge } = useDateOfBirthForm();
    const { dateOfBirth, setIsAgeValid } = useAgeGate();

    React.useEffect(() => {
        if (isAgeValid === true && onSubmit) {
            let dobString: string | undefined;
            if (dateOfBirth) {
                const { year, month, day } = dateOfBirth;
                dobString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            onSubmit(dobString);
        }
    }, [isAgeValid, onSubmit, dateOfBirth]);

    if (isAgeValid === false) {
        return <InvalidAge onClose={onClose} setIsAgeValid={setIsAgeValid} />;
    }

    if (isConfirmingAge) {
        return (
            <StyledGatedContent>
                <div className="alert-icon">
                    <ExclamationCircleIcon size={64} color={'warning'} />
                </div>
                <div className="minor-content">
                    <Loading />
                </div>
            </StyledGatedContent>
        );
    }

    return <DateOfBirthSelection />;
};

export const InvalidGuardianInfo = () => {
    const { t } = useTranslation();
    return (
        <StyledGatedContent>
            <div className="alert-icon">
                <ExclamationCircleIcon size={64} color={'warning'} />
            </div>
            <div className="minor-content">
                <Text variant={'headingLG'}>{t('epic.esport.gated.guardian.invalid.title')}</Text>
                <Text
                    style={{ color: themeVars.color.transparent.light['065'] }}
                    variant="paragraphMD"
                >
                    {t('epic.esport.gated.guardian.invalid.desc')}
                </Text>
            </div>
        </StyledGatedContent>
    );
};

export const DateOfBirthSelection = () => {
    const { t } = useTranslation();
    const { formik, monthOptions, dayOptions, handleFieldChange } = useDateOfBirthForm();

    return (
        <StyledGatedContent>
            <div>
                <Text variant={'headingLG'}>{t('epic.esport.gated.dob.form.title')}</Text>
            </div>
            <form
                onSubmit={formik.handleSubmit}
                style={{ gap: 32, display: 'flex', flexDirection: 'column' }}
            >
                <Text variant={'paragraphMD'} style={{ fontSize: 14 }}>
                    {t('epic.esport.gated.dob.form.desc')}
                </Text>
                <div className="selection-form">
                    <div className="selection-form-container">
                        <Select
                            name={'month'}
                            placeholder={t('epic.esport.gated.dob.form.label.month')}
                            label={t('epic.esport.gated.dob.form.label')}
                            options={monthOptions}
                            value={monthOptions.find(
                                (option) => option.value === formik.values.month
                            )}
                            onChange={(option: { label: string; value: string } | null) =>
                                handleFieldChange('month', option?.value || '')
                            }
                            error={formik.touched.month && Boolean(formik.errors.month)}
                        />
                        <Select
                            name={'day'}
                            placeholder={t('epic.esport.gated.dob.form.label.day')}
                            label={''}
                            options={dayOptions}
                            value={dayOptions.find((option) => option.value === formik.values.day)}
                            onChange={(option: { label: string; value: string } | null) =>
                                handleFieldChange('day', option?.value || '')
                            }
                            error={formik.touched.day && Boolean(formik.errors.day)}
                        />
                        <TextField
                            label={''}
                            name={'year'}
                            placeholder={t('epic.esport.gated.dob.form.label.year')}
                            value={formik.values.year}
                            onChange={(value: string) => handleFieldChange('year', value)}
                            error={formik.touched.year && Boolean(formik.errors.year)}
                        />
                    </div>
                </div>
                <div className="button-action">
                    <Button
                        type="submit"
                        size={'lg'}
                        disabled={
                            !formik.isValid ||
                            !formik?.values?.day ||
                            !formik?.values?.month ||
                            !formik?.values?.year
                        }
                    >
                        {t('epic.common.continue')}
                    </Button>
                </div>
            </form>
        </StyledGatedContent>
    );
};

interface InvalidAgeProps {
    onClose?: () => void;
    setIsAgeValid?: (valid: boolean | null) => void;
}

export const InvalidAge: React.FC<InvalidAgeProps> = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { dateOfBirth, setIsAgeValid } = useAgeGate();
    const { redirectToLogout } = useLogoutRedirect();
    const isConfirmingAge = useSelector(selectIsConfirmingAge);

    const handleButtonClick = async () => {
        if (dateOfBirth) {
            try {
                const { year, month, day } = dateOfBirth;
                const dateOfBirthString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                const result = await dispatch(
                    confirmAge({ dateOfBirth: dateOfBirthString }) as any
                ).unwrap();
                if (result.status === 'OK') {
                    redirectToLogout();
                } else {
                    setIsAgeValid(false);
                }
            } catch (error) {
                if (
                    error === 'errors.com.epicgames.common.oauth.invalid_token' // we should re-login to refresh acc token when account status is invalid
                ) {
                    redirectToLogout();
                }
                console.error('Failed to confirm age:', error);
                setIsAgeValid(false);
            }
        }
    };

    return (
        <StyledGatedContent>
            <div className="alert-icon">
                <ExclamationCircleIcon size={64} color={'warning'} />
            </div>
            <div className="minor-content">
                <Text variant={'headingLG'}>{t('epic.esport.gated.age.invalid.title')}</Text>
                <Text
                    style={{ color: themeVars.color.transparent.light['065'] }}
                    variant="paragraphMD"
                >
                    {t('epic.esport.gated.age.invalid.desc')}
                </Text>
            </div>
            <div className="button-action" style={{ display: 'flex', gap: '12px' }}>
                <Button
                    type="button"
                    size={'lg'}
                    loading={isConfirmingAge}
                    onClick={handleButtonClick}
                    disabled={isConfirmingAge}
                >
                    {t('epic.common.accountportal.default.sign_in')}
                </Button>
            </div>
        </StyledGatedContent>
    );
};
